/**
 * Aishwarya Printers — API Service Layer
 * Base URL: https://aishwarya-backend-55345914059.asia-northeast1.run.app
 */

const BASE_URL = 'https://aishwarya-backend-55345914059.asia-northeast1.run.app';

// Generic fetch helper
async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('token');
  const customBaseUrl = localStorage.getItem('backend_url') || BASE_URL;
  const url = `${customBaseUrl}${path}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    headers,
    ...options,
  });

  if (!res.ok) {
    let errorMsg = `HTTP ${res.status}`;
    try {
      const err = await res.json();
      errorMsg = err.detail || JSON.stringify(err);
    } catch (_) {}
    const error = new Error(errorMsg);
    error.status = res.status;
    throw error;
  }

  // 204 No Content
  if (res.status === 204) return null;

  const text = await res.text();
  if (!text || !text.trim()) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
}

// ─────────────────────────────────────────
// HEALTH
// ─────────────────────────────────────────
export const healthCheck = () => apiFetch('/health');

// ─────────────────────────────────────────
// CLIENTS
// ─────────────────────────────────────────
export const getClients = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return apiFetch(`/api/clients${qs ? '?' + qs : ''}`);
};

export const getClient = (id) =>
  apiFetch(`/api/clients/${id}`);

export const createClient = (data) =>
  apiFetch('/api/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const createAdhocClient = (data) =>
  apiFetch('/api/clients/adhoc', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateClient = (id, data) =>
  apiFetch(`/api/clients/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const deleteClient = (id) =>
  apiFetch(`/api/clients/${id}`, {
    method: 'DELETE',
  });

export const deactivateClient = (id) =>
  apiFetch(`/api/clients/${id}/deactivate`, {
    method: 'PATCH',
  });

export const reactivateClient = (id) =>
  apiFetch(`/api/clients/${id}/reactivate`, {
    method: 'PATCH',
  });

export const getClientByGstin = (gstin) =>
  apiFetch(`/api/clients/match/${gstin}`);

// ─────────────────────────────────────────
// INWARD STOCK
// ─────────────────────────────────────────
export const getInwardStock = () =>
  apiFetch('/api/stock/inward');

export const getInwardStockById = (id) =>
  apiFetch(`/api/stock/inward/${id}`);

export const getInwardImage = (id) =>
  apiFetch(`/api/stock/inward/${id}/image`);

export const confirmInward = (id, data = {}) =>
  apiFetch(`/api/stock/inward/${id}/confirm`, {
    method: 'PATCH',
    body: JSON.stringify({ confirmed_by: 'Admin', ...data }),
  });

export const createClientFromInward = (id, data) =>
  apiFetch(`/api/stock/inward/${id}/create-client`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const rejectInward = (id, reason = '') =>
  apiFetch(`/api/stock/inward/${id}/reject`, {
    method: 'PATCH',
    body: JSON.stringify({ reason }),
  });

export const updateInward = (id, data) =>
  apiFetch(`/api/stock/inward/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

export const deleteInward = (id) =>
  apiFetch(`/api/stock/inward/${id}`, {
    method: 'DELETE',
  });

// ─────────────────────────────────────────
// STOCK LEDGER & BALANCE
// ─────────────────────────────────────────
export const getLedger = (clientId) =>
  apiFetch(`/api/stock/ledger/${clientId}`);

export const getStockBalanceAll = () =>
  apiFetch('/api/stock/balance/all');

export const getStockBalance = (clientId) =>
  apiFetch(`/api/stock/balance/${clientId}`);

export const adjustStock = (data) =>
  apiFetch('/api/stock/adjust', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

// ─────────────────────────────────────────
// WORK CARDS
// ─────────────────────────────────────────
export const getWorkcards = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return apiFetch(`/api/workcards${qs ? '?' + qs : ''}`);
};

export const getWorkcard = (id) =>
  apiFetch(`/api/workcards/${id}`);

export const getWorkcardImage = (id) =>
  apiFetch(`/api/workcards/${id}/image`);

export const approveWorkcard = (id) =>
  apiFetch(`/api/workcards/${id}/approve`, {
    method: 'PATCH',
    body: JSON.stringify({ approved_by: 'Admin' }),
  });

export const rejectWorkcard = (id) =>
  apiFetch(`/api/workcards/${id}/reject`, {
    method: 'PATCH',
    body: JSON.stringify({}),
  });

export const completeWorkcard = (id) =>
  apiFetch(`/api/workcards/${id}/complete`, {
    method: 'PATCH',
    body: JSON.stringify({}),
  });

export const linkClientToWorkcard = (id, client_id) =>
  apiFetch(`/api/workcards/${id}/link-client`, {
    method: 'PATCH',
    body: JSON.stringify({ client_id }),
  });

// ─────────────────────────────────────────
// INVOICES
// ─────────────────────────────────────────
export const getInvoices = () =>
  apiFetch('/api/invoices');

export const getInvoice = (id) =>
  apiFetch(`/api/invoices/${id}`);

export const createInvoice = (data) =>
  apiFetch('/api/invoices', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getInvoicePdfUrl = (id) =>
  apiFetch(`/api/invoices/${id}/pdf-url`);

export const markInvoicePaid = (id) =>
  apiFetch(`/api/invoices/${id}/paid`, {
    method: 'PATCH',
    body: JSON.stringify({}),
  });

export const recordInvoicePayment = (id, data) =>
  apiFetch(`/api/invoices/${id}/payments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getInvoicePayments = (id) =>
  apiFetch(`/api/invoices/${id}/payments`);

export const deleteInvoicePayment = (invoiceId, paymentId) =>
  apiFetch(`/api/invoices/${invoiceId}/payments/${paymentId}`, {
    method: 'DELETE',
  });

export const deleteInvoice = (id) =>
  apiFetch(`/api/invoices/${id}`, {
    method: 'DELETE',
  });

// ─────────────────────────────────────────
// WORK CARDS — Extended
// ─────────────────────────────────────────
export const createWorkcard = (data) =>
  apiFetch('/api/workcards', {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const deleteWorkcard = (id) =>
  apiFetch(`/api/workcards/${id}`, {
    method: 'DELETE',
  });

// ─────────────────────────────────────────
// BOT MANAGEMENT
// ─────────────────────────────────────────
export const getWebhookInfo = () =>
  apiFetch('/api/bot/webhook-info');

export const registerWebhook = (url) =>
  apiFetch('/api/bot/register-webhook', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
